import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	ArrowRightCircle,
	IdCard,
	MapPin,
	Link,
	ShieldCheck,
	ArrowLeft,
	GraduationCap,
	FileBadge,
	Briefcase,
	Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
	Button,
	Input,
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
	InputWrapperAuth,
} from '@/components/ui';
import { maskCNPJ, maskCPF, removeMask } from '@/utils';
import { STORAGE_KEYS } from './StepOne';
import { getStepData, saveStepData } from '@/utils/localStorageUtils';
import { Header } from '@/pages/public/landing/components/Header';
import { useGetProfessionalProfession } from '@/services';

const schema = z
	.object({
		cnpj: z
			.string()
			.transform(removeMask)
			.refine((val) => !val || val.length === 14, 'CNPJ inválido')
			.optional(),
		cpf: z
			.string()
			.transform(removeMask)
			.refine((val) => !val || val.length === 11, 'CPF inválido')
			.optional(),
		profession: z.string().min(1, 'Obrigatório'),
		council_number: z.string().min(1, 'Obrigatório').transform(removeMask),
		document_state: z.string().min(1, 'Obrigatório'),
		rqe: z.string().transform(removeMask).optional(),
		lattes_url: z
			.string()
			.url('URL inválida')
			.max(400)
			.optional()
			.or(z.literal('')),
	})
	.superRefine((val, ctx) => {
		if (!val.cnpj && !val.cpf) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Obrigatório',
				path: ['documentation'],
			});
		}
	});

type FormData = z.infer<typeof schema>;

const FormLabel = ({
	children,
	htmlFor,
	info,
}: {
	children: React.ReactNode;
	htmlFor?: string;
	info?: string;
}) => (
	<div className="mb-1 ml-1 flex items-center gap-1.5">
		<label
			htmlFor={htmlFor}
			className="block cursor-pointer text-[10px] font-black uppercase tracking-widest text-stone-500 transition-colors hover:text-stone-700"
		>
			{children}
		</label>

		{info && (
			<div className="group relative flex items-center">
				<Info
					size={12}
					className="cursor-help text-stone-300 transition-colors hover:text-stone-500"
				/>
				<div className="animate-in fade-in zoom-in absolute bottom-full left-0 z-50 mb-2 hidden w-48 rounded-xl bg-stone-900 p-2.5 text-[10px] font-bold leading-tight text-white shadow-xl duration-200 group-hover:block">
					{info}
					<div className="absolute left-2 top-full -translate-y-1 border-8 border-transparent border-t-stone-900" />
				</div>
			</div>
		)}
	</div>
);

const SlidingToggle = ({
	options,
	value,
	onChange,
}: {
	options: { value: string; label?: string; children?: React.ReactNode }[];
	value?: string;
	onChange: (val: string) => void;
}) => {
	const activeIndex = options.findIndex((o) => o.value === value);
	const count = options.length;

	return (
		<div className="w-full space-y-3">
			<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
				<div
					className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
					style={{
						width: `calc((100% - 8px) / ${count})`,
						left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
					}}
				/>
				{options.map((opt) => (
					<button
						key={opt.value}
						type="button"
						onClick={() => onChange(opt.value)}
						className={`
							z-10 flex-1 cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-xs font-bold outline-none ring-0
							transition-colors duration-200 focus:outline-none focus:ring-0
							${
								value === opt.value
									? 'text-stone-900'
									: 'text-stone-400 hover:text-stone-600'
							}
						`}
					>
						{opt.label || opt.value}
					</button>
				))}
			</div>
			<div className="animate-in fade-in slide-in-from-top-1 duration-300">
				{options.find((o) => o.value === value)?.children}
			</div>
		</div>
	);
};

const StepTwo = () => {
	const navigate = useNavigate();
	const [selectedDocType, setSelectedDocType] = useState<string>('CPF');

	const { data: professionsData } = useGetProfessionalProfession();

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	useEffect(() => {
		const savedData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_DOCUMENTATION,
		);
		if (savedData) {
			Object.entries(savedData).forEach(([key, value]) => {
				setValue(key as keyof FormData, value as any);
			});
			if (savedData.cpf) setSelectedDocType('CPF');
			else if (savedData.cnpj) setSelectedDocType('CNPJ');
		}
	}, [setValue]);

	const onSubmit = (data: FormData) => {
		saveStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_DOCUMENTATION,
			data,
		);
		navigate('/profissional/cadastro/info-contato');
	};

	const iconCommonClass = 'w-4 h-4 text-stone-400';
	const states = [
		'AC',
		'AL',
		'AP',
		'AM',
		'BA',
		'CE',
		'DF',
		'ES',
		'GO',
		'MA',
		'MT',
		'MS',
		'MG',
		'PA',
		'PB',
		'PR',
		'PE',
		'PI',
		'RJ',
		'RN',
		'RS',
		'RO',
		'RR',
		'SC',
		'SP',
		'SE',
		'TO',
	];

	const selectedProfession = professionsData?.find(
		(p: any) => p.id === watch('profession'),
	);

	const optionsDocument = [
		{
			value: 'CPF',
			children: (
				<InputWrapperAuth
					icon={<IdCard className={iconCommonClass} />}
					error={errors.cpf?.message}
					value={watch('cpf')}
					displayError
				>
					<Input
						{...register('cpf')}
						id="field-cpf"
						placeholder="000.000.000-00"
						className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
						value={maskCPF(watch('cpf') || '')}
					/>
				</InputWrapperAuth>
			),
		},
		{
			value: 'CNPJ',
			children: (
				<InputWrapperAuth
					icon={<IdCard className={iconCommonClass} />}
					error={errors.cnpj?.message}
					value={watch('cnpj')}
					displayError
				>
					<Input
						{...register('cnpj')}
						id="field-cnpj"
						placeholder="00.000.000/0000-00"
						className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
						value={maskCNPJ(watch('cnpj') || '')}
					/>
				</InputWrapperAuth>
			),
		},
	];

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Documentação | Plumaa</title>
			</Helmet>
			<Header />

			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-4 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					<div className="space-y-0.5 text-center">
						<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
							Documentação
						</h1>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							Etapa 2 de 4: Valide sua atuação profissional
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-5"
					>
						{/* TIPO DE DOCUMENTO */}
						<div>
							<FormLabel
								htmlFor={
									selectedDocType === 'CPF'
										? 'field-cpf'
										: 'field-cnpj'
								}
							>
								Tipo de Documento
							</FormLabel>
							<SlidingToggle
								options={optionsDocument}
								value={selectedDocType}
								onChange={(val) => {
									setSelectedDocType(val);
									setValue('cnpj', '');
									setValue('cpf', '');
								}}
							/>
						</div>

						{/* PROFISSÃO */}
						<div>
							<FormLabel htmlFor="field-profession">
								Profissão
							</FormLabel>
							<InputWrapperAuth
								icon={<Briefcase className={iconCommonClass} />}
								value={watch('profession')}
								error={errors.profession?.message}
								displayError={true}
							>
								<Select
									value={watch('profession')}
									onValueChange={(v) =>
										setValue('profession', v)
									}
								>
									<SelectTrigger
										id="field-profession"
										className="flex h-9 w-full flex-1 items-center justify-between border-none bg-transparent p-0 text-sm font-normal shadow-none focus:ring-0"
									>
										<SelectValue className="flex-1 text-left">
											{selectedProfession ? (
												<span className="text-stone-600">
													{
														selectedProfession.service_name
													}
												</span>
											) : (
												<span className="text-stone-400">
													Selecione sua profissão
												</span>
											)}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{professionsData?.map(
												(item: any) => (
													<SelectItem
														key={item.id}
														value={item.id}
													>
														{item.service_name}
													</SelectItem>
												),
											)}
										</SelectGroup>
									</SelectContent>
								</Select>
							</InputWrapperAuth>
						</div>

						{/* REGISTRO PROFISSIONAL */}
						<div>
							<FormLabel htmlFor="field-council">
								Registro Profissional (CRM/CRP)
							</FormLabel>
							<InputWrapperAuth
								icon={<FileBadge className={iconCommonClass} />}
								error={errors.council_number?.message}
								value={watch('council_number')}
								displayError
							>
								<Input
									{...register('council_number')}
									id="field-council"
									placeholder="000000"
									className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
								/>
							</InputWrapperAuth>
						</div>

						{/* RQE */}
						<div>
							<FormLabel htmlFor="field-rqe">
								RQE (opcional)
							</FormLabel>
							<InputWrapperAuth
								icon={
									<GraduationCap
										className={iconCommonClass}
									/>
								}
								error={errors.rqe?.message}
								value={watch('rqe')}
								displayError
							>
								<Input
									{...register('rqe')}
									id="field-rqe"
									placeholder="Registro de Especialista"
									className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
								/>
							</InputWrapperAuth>
						</div>

						{/* ESTADO DE ATUAÇÃO */}
						<div>
							<FormLabel htmlFor="field-state">
								Estado de Atuação
							</FormLabel>
							<InputWrapperAuth
								icon={<MapPin className={iconCommonClass} />}
								value={watch('document_state')}
								displayError={false}
							>
								<Select
									value={watch('document_state')}
									onValueChange={(v) =>
										setValue('document_state', v)
									}
								>
									<SelectTrigger
										id="field-state"
										className="flex h-9 w-full flex-1 items-center justify-between border-none bg-transparent p-0 text-sm font-normal shadow-none focus:ring-0"
									>
										<SelectValue className="flex-1 text-left">
											{watch('document_state') ? (
												<span className="text-stone-600">
													{watch('document_state')}
												</span>
											) : (
												<span className="text-stone-400">
													Selecione o Estado (UF)
												</span>
											)}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{states.map((s) => (
												<SelectItem key={s} value={s}>
													{s}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</InputWrapperAuth>
						</div>

						{/* LINK CURRÍCULO */}
						<div>
							<FormLabel
								htmlFor="field-lattes"
								info="Lattes, CV, LinkedIn e outros"
							>
								Link do currículo (opcional)
							</FormLabel>
							<InputWrapperAuth
								icon={<Link className={iconCommonClass} />}
								error={errors.lattes_url?.message}
								value={watch('lattes_url')}
								displayError
							>
								<Input
									{...register('lattes_url')}
									id="field-lattes"
									placeholder="https://..."
									className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
								/>
							</InputWrapperAuth>
						</div>

						<div className="space-y-4 pt-2">
							<Button
								type="submit"
								className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-stone-800"
								text="Próxima Etapa"
								endIcon={
									<ArrowRightCircle
										size={14}
										className="ml-1"
									/>
								}
							/>
							<div className="text-center">
								<button
									type="button"
									onClick={() => navigate(-1)}
									className="mx-auto flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-400 transition-colors hover:text-stone-900"
								>
									<ArrowLeft size={12} />
									Voltar para etapa anterior
								</button>
							</div>
						</div>

						<div className="mt-2 flex items-center justify-center gap-2 border-t border-stone-50 pt-4 text-[9px] font-black uppercase tracking-widest text-stone-300">
							<ShieldCheck size={14} />
							<span>Seus dados estão protegidos pela Plumaa</span>
						</div>
					</form>
				</div>
			</main>
		</div>
	);
};

export { StepTwo };
